import { useEnumHelpers } from '@/queries/useEnumHelpers';
import {
  Add,
  Clear,
  Cloud,
  CloudOff,
  MonetizationOn,
  Paid,
  Remove,
  RequestQuote,
  Schedule,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { PaperTooltip } from '../../components/PaperTooltip.tsx';
import {
  safeFormatDecimal,
  safeFormatPercent,
  safeFormatUSD,
} from '../../utils.ts';
import { SystemCardInputField } from '../components/SystemCardInputField';
import { SystemCardPieChart } from '../components/SystemCardPieChart';
import { EnergyKind, ExternalEnergy, OnsiteEnergy } from '../types';
import { iconForEnergySink } from '../utils.ts';
import { isExternalEnergy, isOnsiteEnergy } from './energyUtils';

// Shared style ratios — keep in sync with SystemCard.tsx
const CARD_PADDING_PCT = '6%';
const CARD_BORDER_RADIUS_RATIO = 12 / 266;
const TEXT_SIZE_RATIO = 12 / 266;
const HEADER_HEIGHT_RATIO = 20 / 266;
const SVG_AND_NAME_HEIGHT_RATIO = 110 / 266;
const SVG_AND_NAME_SPACING_RATIO = 10 / 266;
const SVG_HEIGHT_WIDTH_RATIO = 74 / 266;
const BUTTON_SELECTED_CONTAINER_HEIGHT_RATIO = 42 / 266;
const BUTTON_SELECTED_FONT_SIZE_RATIO = 26 / 266;
const BUTTON_SELECTED_CONTAINER_SPACING_RATIO = 11 / 266;
const BUTTON_SELECTED_WIDTH_RATIO = 40 / 266;
const BUTTON_BORDER_RADIUS_RATIO = 4 / 266;
const BUTTON_NOT_SELECTED_WIDTH_RATIO = (77 / 198) * (198 / 266);
const BUTTON_NOT_SELECTED_TEXT_SIZE_RATIO = 15 / 266;
const FOOTER_CONTAINER_HEIGHT_RATIO = 35 / 266;
const PIE_CHART_SIZE_RATIO = 28 / 266;

interface EnergyUnitProps {
  normalizedCapital: number;
  normalizedEnergy: number;
  unit: OnsiteEnergy | ExternalEnergy;
  limit: number;
  onAdd: (techId: string) => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onChange: (value: number) => void;
  totalCount: number;
  inEdit?: boolean;
  readOnly?: boolean;
}

interface InternalCardProps {
  normalizedCapital: number;
  unit: OnsiteEnergy | ExternalEnergy;
  limit: number;
  onAdd: (techId: string) => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onChange: (value: number) => void;
  totalCount: number;
  inEdit?: boolean;
  readOnly?: boolean;
  isRenewable: boolean;
  cardHeight: number;
}

const OnsiteEnergyCard: React.FC<InternalCardProps> = ({
  normalizedCapital,
  unit,
  limit,
  onAdd,
  onIncrease,
  onDecrease,
  onChange,
  totalCount,
  inEdit,
  readOnly,
  isRenewable,
  cardHeight,
}) => {
  const Icon = iconForEnergySink(
    (unit as OnsiteEnergy).energySource as EnergyKind,
  );

  return (
    <Stack
      justifyContent={'space-between'}
      width={'100%'}
      height={'100%'}
      spacing={0}
    >
      {/* Header row */}
      <Stack
        justifyContent={'space-between'}
        alignItems={'center'}
        direction={'row'}
        flex={`${HEADER_HEIGHT_RATIO} 1 0`}
        minHeight={0}
        sx={{ fontSize: `${cardHeight * HEADER_HEIGHT_RATIO}px` }}
      >
        <Stack direction={'row'} spacing={0}>
          {normalizedCapital > 0 &&
            Array.from({ length: normalizedCapital }).map((_, idx) => (
              <Paid key={idx} fontSize={'inherit'} color={'info'} />
            ))}
        </Stack>
        <Stack direction={'row'} spacing={0}>
          {isRenewable ? (
            <CloudOff fontSize={'inherit'} color={'info'} />
          ) : (
            <Cloud fontSize={'inherit'} color={'info'} />
          )}
        </Stack>
      </Stack>
      <PaperTooltip
        disableInteractive
        title={<EnergyUnitTooltip unit={unit} limit={limit} />}
        placement={'right'}
      >
        {/* Body section */}
        <Stack
          direction={'column'}
          flex={SVG_AND_NAME_HEIGHT_RATIO}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Stack
            justifyContent={'center'}
            alignItems={'center'}
            spacing={`${SVG_AND_NAME_SPACING_RATIO}%`}
          >
            <Box
              sx={{
                height: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
                width: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
                fontSize: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
              }}
            >
              <Icon fontSize={'inherit'} color={'success'} />
            </Box>
          </Stack>
          <Typography
            sx={{ width: '100%' }}
            align={'center'}
            textTransform={'uppercase'}
            variant={'subtitle2'}
            fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
            noWrap
          >
            {unit.name}
          </Typography>
        </Stack>
      </PaperTooltip>
      {/* Action section */}
      <Stack
        alignItems={'center'}
        flex={BUTTON_SELECTED_CONTAINER_HEIGHT_RATIO}
      >
        {!inEdit && (
          <Button
            color={'success'}
            variant={'contained'}
            onClick={() => onAdd(String(unit.id))}
            sx={{
              minWidth: 0,
              borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
              width: `${cardHeight * BUTTON_NOT_SELECTED_WIDTH_RATIO}px`,
              '&:hover': {
                backgroundColor: 'info.main',
                color: 'success.main',
              },
            }}
          >
            <Typography
              fontSize={`${cardHeight * BUTTON_NOT_SELECTED_TEXT_SIZE_RATIO}px`}
              variant={'subtitle2'}
            >
              ADD
            </Typography>
          </Button>
        )}
        {inEdit && readOnly && (
          <Box
            sx={{
              width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
              height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
              borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
              bgcolor: 'secondary.main',
              color: 'info.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: 'inherit',
            }}
          >
            <Typography fontSize={'inherit'} fontWeight={'bold'}>
              {totalCount}
            </Typography>
          </Box>
        )}
        {inEdit && !readOnly && (
          <Stack
            direction={'row'}
            fontSize={`${cardHeight * BUTTON_SELECTED_FONT_SIZE_RATIO}px`}
            spacing={`${cardHeight * BUTTON_SELECTED_CONTAINER_SPACING_RATIO}px`}
          >
            {totalCount === 1 && (
              <Button
                variant={'contained'}
                color={'warning'}
                size={'small'}
                sx={{
                  p: 0,
                  minWidth: 0,
                  width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                  height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                  fontSize: 'inherit',
                  borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                }}
                onClick={onDecrease}
              >
                <Clear fontSize={'inherit'} color={'secondary'} />
              </Button>
            )}
            {totalCount > 1 && (
              <Button
                variant={'contained'}
                color={'info'}
                size={'small'}
                sx={{
                  p: 0,
                  minWidth: 0,
                  width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                  height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                  borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                  fontSize: 'inherit',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={onDecrease}
              >
                <Remove color={'secondary'} />
              </Button>
            )}
            <Box flex={1}>
              <SystemCardInputField
                onChangeCount={onChange}
                cardHeight={cardHeight * BUTTON_SELECTED_WIDTH_RATIO}
                borderRadius={cardHeight * BUTTON_BORDER_RADIUS_RATIO}
                itemCount={totalCount}
              />
            </Box>
            <Button
              variant={'contained'}
              color={totalCount < limit ? 'info' : 'warning'}
              disabled={totalCount >= limit}
              onClick={onIncrease}
              sx={{
                p: 0,
                minWidth: 0,
                width: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                height: `${cardHeight * BUTTON_SELECTED_WIDTH_RATIO}px`,
                fontSize: 'inherit',
                '&:hover': { backgroundColor: 'action.hover' },
                borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
                opacity: totalCount >= limit ? 0.6 : 1,
              }}
            >
              <Add fontSize={'inherit'} color={'secondary'} />
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Footer section */}
      <Stack
        justifyContent={'space-between'}
        direction={'row'}
        alignItems={'center'}
        flex={FOOTER_CONTAINER_HEIGHT_RATIO}
      >
        <SystemCardPieChart
          width={cardHeight * PIE_CHART_SIZE_RATIO}
          coverage={limit > 0 ? 1 / limit : 0}
        />
        <Typography
          variant={'subtitle2'}
          fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
        >
          LIMIT {Math.max(1, Math.round(limit))}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ExternalEnergyCard: React.FC<InternalCardProps> = ({
  normalizedCapital,
  unit,
  onAdd,
  onDecrease,
  totalCount,
  inEdit,
  readOnly,
  isRenewable,
  cardHeight,
}) => {
  const Icon = iconForEnergySink(
    (unit as ExternalEnergy).energyProvider as EnergyKind,
  );

  return (
    <Stack
      justifyContent={'space-between'}
      width={'100%'}
      height={'100%'}
      spacing={0}
    >
      {/* Header row */}
      <Stack
        justifyContent={'space-between'}
        alignItems={'center'}
        direction={'row'}
        flex={`${HEADER_HEIGHT_RATIO} 1 0`}
        minHeight={0}
        sx={{ fontSize: `${cardHeight * HEADER_HEIGHT_RATIO}px` }}
      >
        <Stack direction={'row'} spacing={0}>
          {normalizedCapital > 0 &&
            Array.from({ length: normalizedCapital }).map((_, idx) => (
              <Paid key={idx} fontSize={'inherit'} color={'info'} />
            ))}
        </Stack>
        <Stack direction={'row'} spacing={0}>
          {isRenewable ? (
            <CloudOff fontSize={'inherit'} color={'info'} />
          ) : (
            <Cloud fontSize={'inherit'} color={'info'} />
          )}
        </Stack>
      </Stack>
      <PaperTooltip
        disableInteractive
        title={<EnergyUnitTooltip unit={unit} limit={0} />}
        placement={'right'}
      >
        {/* Body section */}
        <Stack
          direction={'column'}
          flex={SVG_AND_NAME_HEIGHT_RATIO}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Stack
            justifyContent={'center'}
            alignItems={'center'}
            spacing={`${SVG_AND_NAME_SPACING_RATIO}%`}
          >
            <Box
              sx={{
                height: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
                width: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
                fontSize: `${cardHeight * SVG_HEIGHT_WIDTH_RATIO}px`,
              }}
            >
              <Icon fontSize={'inherit'} color={'success'} />
            </Box>
          </Stack>
          <Typography
            sx={{ width: '100%' }}
            align={'center'}
            textTransform={'uppercase'}
            variant={'subtitle2'}
            fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
            noWrap
          >
            {unit.name}
          </Typography>
        </Stack>
      </PaperTooltip>
      {/* Action section */}
      <Stack
        alignItems={'center'}
        flex={BUTTON_SELECTED_CONTAINER_HEIGHT_RATIO}
      >
        {!inEdit && (
          <Button
            color={'success'}
            variant={'contained'}
            onClick={() => onAdd(String(unit.id))}
            sx={{
              minWidth: 0,
              borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
              width: `${cardHeight * BUTTON_NOT_SELECTED_WIDTH_RATIO}px`,
              '&:hover': {
                backgroundColor: 'info.main',
                color: 'success.main',
              },
            }}
          >
            <Typography
              fontSize={`${cardHeight * BUTTON_NOT_SELECTED_TEXT_SIZE_RATIO}px`}
              variant={'subtitle2'}
            >
              ADD
            </Typography>
          </Button>
        )}
        {inEdit && !readOnly && (
          <Button
            variant={'contained'}
            color={totalCount <= 1 ? 'warning' : 'secondary'}
            onClick={onDecrease}
            sx={{
              borderRadius: `${cardHeight * BUTTON_BORDER_RADIUS_RATIO}px`,
            }}
          >
            <Typography
              fontSize={`${cardHeight * BUTTON_NOT_SELECTED_TEXT_SIZE_RATIO}px`}
              variant={'subtitle2'}
            >
              REMOVE
            </Typography>
          </Button>
        )}
      </Stack>

      {/* Footer section */}
      <Stack
        justifyContent={'center'}
        direction={'row'}
        alignItems={'center'}
        flex={FOOTER_CONTAINER_HEIGHT_RATIO}
      >
        <Typography
          variant={'subtitle2'}
          fontSize={`${cardHeight * TEXT_SIZE_RATIO}px`}
        >
          External Provider
        </Typography>
      </Stack>
    </Stack>
  );
};

export const EnergyUnit: React.FC<EnergyUnitProps> = React.memo(
  ({
    normalizedCapital,
    unit,
    limit,
    onAdd,
    onChange,
    onIncrease,
    onDecrease,
    inEdit,
    totalCount,
    readOnly,
  }) => {
    const isRenewable = isOnsiteEnergy(unit)
      ? !unit.tco2eMwh || unit.tco2eMwh === '0'
      : false;

    const cardRef = useRef<HTMLDivElement | null>(null);
    const [cardHeight, setCardHeight] = useState(0);

    useEffect(() => {
      const updateCardHeight = () => {
        if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
      };
      updateCardHeight();
      window.addEventListener('resize', updateCardHeight);
      return () => window.removeEventListener('resize', updateCardHeight);
    }, []);

    const sharedProps: InternalCardProps = {
      normalizedCapital,
      unit,
      limit,
      onAdd,
      onIncrease,
      onDecrease,
      onChange,
      totalCount,
      inEdit,
      readOnly,
      isRenewable,
      cardHeight,
    };

    return (
      <Card
        elevation={8}
        ref={cardRef}
        data-id={unit.id}
        sx={{
          backgroundColor: 'background.card',
          backgroundImage: 'url(/img/paper-texture.png)',
          opacity: 0.9,
          borderRadius: `${cardHeight * CARD_BORDER_RADIUS_RATIO}px`,
          p: CARD_PADDING_PCT,
          aspectRatio: '198 / 266',
          height: '100%',
          width: '100%'
        }}
      >
        <Box sx={{ height: '100%', width: '100%' }}>
          {isOnsiteEnergy(unit) ? (
            <OnsiteEnergyCard {...sharedProps} />
          ) : (
            <ExternalEnergyCard {...sharedProps} />
          )}
        </Box>
      </Card>
    );
  },
);

EnergyUnit.displayName = 'EnergyUnit';

interface UnitTooltipProps {
  unit: ExternalEnergy | OnsiteEnergy;
  limit: number;
}
const EnergyUnitTooltip: React.FC<UnitTooltipProps> = ({ unit, limit }) => {
  const chartSize = 17;
  const { energySources } = useEnumHelpers();

  const sink = isExternalEnergy(unit) ? unit.energyProvider : unit.energySource;
  const Icon = iconForEnergySink(sink as EnergyKind);
  const ElectricityIcon = iconForEnergySink('Electricity' as EnergyKind)
  return (
    <Stack
      spacing={{ sm: 1, md: 2 }}
      fontFamily={'subtitle1.fontFamily'}
      fontSize={{ sm: 8, md: 10, lg: 12 }}
      fontWeight={500}
    >
      {/** TITLE */}
      <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
        <Box flex={1}>
          <Typography
            variant={'subtitle2'}
            textTransform={'uppercase'}
            fontFamily={'inherit'}
            fontSize={{ sm: 11, md: 14, lg: 16 }}
            lineHeight={1}
          >
            {unit.name}
          </Typography>
        </Box>
        <Stack
          direction={'row'}
          spacing={0.5}
          alignItems={'center'}
          alignSelf={'flex-start'}
        >
          <Icon />
          <Typography fontSize={'inherit'} variant={'subtitle2'}>
            {energySources.find((k) => k.name === sink)?.value}
          </Typography>
        </Stack>
      </Stack>
      <Stack width={'100%'} spacing={1}>
        {isOnsiteEnergy(unit) && (
          <>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            spacing={1}
          >
            {/**first row - coveragefield Coverage*/}
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <SystemCardPieChart
                  width={chartSize}
                  coverage={limit > 0 ? 1 / limit : 0}
                />
              <Typography
                fontSize={'inherit'}
                fontFamily={'inherit'}
                fontWeight={'inherit'}
                textTransform={'capitalize'}
              >
                Energy Coverage
              </Typography>
            </Stack>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'bold'}
            >
                {safeFormatPercent(
                  limit > 0 ? 1 / limit : 0,
                  'EnergyTooltip.coverage',
                )}
                /Unit
            </Typography>
          </Stack>
          {/**second row - Energy Production*/}
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            spacing={1}
          >
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <ElectricityIcon fontSize={'small'} color={'primary'}/>
              <Typography
                fontSize={'inherit'}
                fontFamily={'inherit'}
                fontWeight={'inherit'}
              >
                Energy Production
              </Typography>
            </Stack>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'bold'}
            >
              {safeFormatDecimal(
                unit.annualEnergyProduced,
                'EnergyTooltip.annualEnergyProduced',
              )}{' '}
              MWh
            </Typography>
          </Stack>
          </>
        )}
        {/**third row - Annual GHG Emissions*/}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <Cloud fontSize={'small'} color={'primary'} />
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
            >
              Annual GHG Emissions
            </Typography>
          </Stack>
          {/* emission is 0 ?*/}
          {(!unit.tco2eMwh || Number(unit.tco2eMwh) === 0) 
          ? (
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {'\u2014'}
          </Typography>) 
          : (
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
            align={'right'}
          >
            {safeFormatDecimal(unit.tco2eMwh, 'EnergyTooltip.tco2eMwh')} tCO<sub>2</sub>e/MWh
          </Typography>)}
        </Stack>
        {isOnsiteEnergy(unit) && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            spacing={1}
          >
          {/**fourth row - Capital Cost*/}
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <MonetizationOn fontSize={'small'} color={'primary'}/>
              <Typography
                fontSize={'inherit'}
                fontFamily={'inherit'}
                fontWeight={'inherit'}
              >
                Capital Cost
              </Typography>
            </Stack>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'bold'}
            >
              {safeFormatUSD(
                unit.capitalCostUnit
                  ? 1000 * Number(unit.capitalCostUnit)
                  : undefined,
                'EnergyTooltip.capitalCostUnit',
              )}
            </Typography>
          </Stack>
        )}
        {/**fifth row - Annual Cost*/}{' '}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <RequestQuote fontSize={'small'} color={'primary'}/>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
            >
              Annual Cost
            </Typography>
          </Stack>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {isOnsiteEnergy(unit)
              ? `${safeFormatUSD(
                  unit.maintenanceCostUnit
                    ? 1000 * Number(unit.maintenanceCostUnit)
                    : undefined,
                  'EnergyTooltip.maintenanceCostUnit',
                )}/unit/Year`
              : safeFormatUSD(unit.costPerMWh, 'EnergyTooltip.costPerMWh')}
          </Typography>
        </Stack>
        {isOnsiteEnergy(unit) && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            spacing={1}
          >
            {/**sixth row - Design Life*/}
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Schedule fontSize={'small'} color={'primary'}/>
              <Typography
                fontSize={'inherit'}
                fontFamily={'inherit'}
                fontWeight={'inherit'}
              >
                Design Life
              </Typography>
            </Stack>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'bold'}
            >
              {unit.designLife
                ? `${safeFormatDecimal(unit.designLife, 'EnergyTooltip.designLife')} years`
                : '\u2014'}
            </Typography>
          </Stack>
        )}
        {isExternalEnergy(unit) && unit.notes && (
          <Box>
            <Typography>Rate Plan</Typography>
            <Typography fontWeight={'bold'}>{unit.notes}</Typography>
          </Box>
        )}
      </Stack>
      {isOnsiteEnergy(unit) && (
      <>
        {/** DIVIDER */}
        <Stack direction={'row'} justifyContent={'center'} width={'100%'}>
          <Divider
            orientation={'horizontal'}
            sx={{ width: '35%', color: 'text.secondary' }}
            flexItem
          />
        </Stack>
        {/** FOOTER / LIMIT / LIMIT DISCLAIMER TEXT */}
        <Stack spacing={0.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={500}
            >
              Limit
            </Typography>
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'bold'}
            >
              {limit ?? '\u2014'}
            </Typography>
          </Box>
          <Typography
            variant={'subtitle2'}
            textAlign={'center'}
            textTransform={'uppercase'}
            lineHeight={1.3}
            fontSize={{ sm: 4, md: 6, lg: 8 }}
          >
            Limits on components may be due to maximum required to meet needs,
            insufficient space to support more, or additional parameters.
          </Typography>
        </Stack>{' '}
      </>
      )}
    </Stack>
  );
};
